import { useState, useEffect, useCallback, useRef } from 'react';
import { institutionService } from '../services/api/institutions';
import { ApiClientError } from '../services/api/client';
import { waitForAuth, isAuthReady } from '../services/auth/authService';
import type { Institution, GetInstitutionsParams } from '../types/institution';

// Hook state interface
interface UseInstitutionsState {
  institutions: Institution[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
}

// Hook return interface
interface UseInstitutionsReturn extends UseInstitutionsState {
  fetchInstitutions: (params?: GetInstitutionsParams) => Promise<void>;
  searchInstitutions: (searchTerm: string) => Promise<void>;
  clearError: () => void;
  refresh: () => Promise<void>;
}

// Cache interface for storing results
interface InstitutionsCache {
  data: Institution[];
  timestamp: number;
  params: string;
}

// Cache duration in milliseconds (5 minutes)
const CACHE_DURATION = 5 * 60 * 1000;

// Simple in-memory cache
const institutionsCache = new Map<string, InstitutionsCache>();

// Generate cache key from parameters
const getCacheKey = (params?: GetInstitutionsParams): string => {
  return JSON.stringify(params || {});
};

// Check if cache is valid
const isCacheValid = (cache: InstitutionsCache): boolean => {
  return Date.now() - cache.timestamp < CACHE_DURATION;
};

/**
 * Custom hook for managing institutions data
 * @param initialParams - Initial parameters for fetching institutions
 * @param enableCache - Whether to enable caching (default: true)
 * @returns Hook state and methods
 */
export const useInstitutions = (
  initialParams?: GetInstitutionsParams,
  enableCache: boolean = true
): UseInstitutionsReturn => {
  const [state, setState] = useState<UseInstitutionsState>({
    institutions: [],
    loading: false,
    error: null,
    hasMore: true,
  });

  const abortControllerRef = useRef<AbortController | null>(null);
  const lastParamsRef = useRef<GetInstitutionsParams | undefined>(initialParams);

  // Clear error function
  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  // Generic fetch function with caching
  const fetchInstitutions = useCallback(async (params?: GetInstitutionsParams) => {
    // Cancel any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Wait for auth to be ready before making API calls
    await waitForAuth();

    const cacheKey = getCacheKey(params);
    
    // Check cache first if enabled
    if (enableCache) {
      const cached = institutionsCache.get(cacheKey);
      if (cached && isCacheValid(cached)) {
        setState(prev => ({
          ...prev,
          institutions: cached.data,
          loading: false,
          error: null,
          hasMore: cached.data.length >= (params?.limit || 50),
        }));
        return;
      }
    }

    setState(prev => ({ ...prev, loading: true, error: null }));
    lastParamsRef.current = params;

    try {
      const institutions = await institutionService.getInstitutions(params);
      
      // Cache the results if enabled
      if (enableCache) {
        institutionsCache.set(cacheKey, {
          data: institutions,
          timestamp: Date.now(),
          params: cacheKey,
        });
      }

      setState(prev => ({
        ...prev,
        institutions,
        loading: false,
        error: null,
        hasMore: institutions.length >= (params?.limit || 50),
      }));
    } catch (error) {
      let errorMessage = "We're experiencing some technical difficulties. Please try again later.";
       
      setState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage,
        hasMore: false,
      }));
    }
  }, [enableCache]);

  // Search function with debouncing logic
  const searchInstitutions = useCallback(async (searchTerm: string) => {
    const searchParams: GetInstitutionsParams = {
      search: searchTerm.trim(),
      limit: 5,
    };

    await fetchInstitutions(searchParams);
  }, [fetchInstitutions]);

  // Refresh function to reload with last parameters
  const refresh = useCallback(async () => {
    // Clear cache for current params if caching is enabled
    if (enableCache && lastParamsRef.current) {
      const cacheKey = getCacheKey(lastParamsRef.current);
      institutionsCache.delete(cacheKey);
    }
    
    await fetchInstitutions(lastParamsRef.current);
  }, [fetchInstitutions, enableCache]);

  // Initial fetch on mount - only after auth is ready
  useEffect(() => {
    const initializeFetch = async () => {
      // Wait for auth to be ready before making any API calls
      await waitForAuth();
      
      // Double-check auth is ready before proceeding
      if (!isAuthReady()) {
        console.warn('Auth not ready, skipping initial institutions fetch');
        return;
      }
      
      if (initialParams || (!initialParams && state.institutions.length === 0)) {
        fetchInstitutions(initialParams);
      }
    };

    initializeFetch();

    // Cleanup function
    return () => {
      const controller = abortControllerRef.current;
      if (controller) {
        controller.abort();
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array for mount-only effect

  return {
    ...state,
    fetchInstitutions,
    searchInstitutions,
    clearError,
    refresh,
  };
};

/**
 * Hook for getting popular institutions
 * @param limit - Number of institutions to fetch
 * @returns Hook state and methods for popular institutions
 */
export const usePopularInstitutions = (limit: number = 5) => {
  const [state, setState] = useState<UseInstitutionsState>({
    institutions: [],
    loading: false,
    error: null,
    hasMore: false,
  });

  const fetchPopularInstitutions = useCallback(async () => {
    // Wait for auth to be ready before making API calls
    await waitForAuth();
    
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const institutions = await institutionService.getPopularInstitutions(limit);
      setState(prev => ({
        ...prev,
        institutions,
        loading: false,
        error: null,
        hasMore: false,
      }));
    } catch (error) {
      const errorMessage = "We're experiencing some technical difficulties. Please try again later.";
      
      setState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage,
        hasMore: false,
      }));
    }
  }, [limit]);

  useEffect(() => {
    fetchPopularInstitutions();
  }, [fetchPopularInstitutions]);

  return {
    ...state,
    refresh: fetchPopularInstitutions,
  };
};

/**
 * Hook for getting a specific institution by ID
 * @param institutionId - The institution ID to fetch
 * @returns Hook state with single institution
 */
export const useInstitution = (institutionId: string | null) => {
  const [state, setState] = useState<{
    institution: Institution | null;
    loading: boolean;
    error: string | null;
  }>({
    institution: null,
    loading: false,
    error: null,
  });

  const fetchInstitution = useCallback(async (id: string) => {
    // Wait for auth to be ready before making API calls
    await waitForAuth();
    
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const institution = await institutionService.getInstitutionById(id);
      setState(prev => ({
        ...prev,
        institution,
        loading: false,
        error: null,
      }));
    } catch (error) {
      const errorMessage = "We're experiencing some technical difficulties. Please try again later.";
      
      setState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage,
      }));
    }
  }, []);

  useEffect(() => {
    if (institutionId) {
      fetchInstitution(institutionId);
    } else {
      setState({
        institution: null,
        loading: false,
        error: null,
      });
    }
  }, [institutionId, fetchInstitution]);

  return {
    ...state,
    refresh: institutionId ? () => fetchInstitution(institutionId) : undefined,
  };
}; 